import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cx } from '../lib/cx';

const ToastContext = createContext(null);

const TONE_STYLES = {
  success: { icon: CheckCircle2, className: 'text-success' },
  error: { icon: AlertTriangle, className: 'text-danger' },
  info: { icon: Info, className: 'text-info' },
};

/**
 * Non-blocking notifications (replaces window.alert). Toasts are announced
 * to screen readers via live regions: errors assertively, the rest politely.
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(({ title, description = '', tone = 'info', duration }) => {
    const id = nextId.current++;
    setToasts((list) => [...list.slice(-3), { id, title, description, tone }]);
    const ms = duration ?? (tone === 'error' ? 8000 : 4500);
    if (ms > 0) window.setTimeout(() => dismiss(id), ms);
    return id;
  }, [dismiss]);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  const renderToast = (t) => {
    const { icon: Icon, className } = TONE_STYLES[t.tone] || TONE_STYLES.info;
    return (
      <div
        key={t.id}
        className="pointer-events-auto flex w-full items-start gap-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-overlay animate-pop-in"
      >
        <Icon size={18} className={cx('mt-0.5 shrink-0', className)} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-fg">{t.title}</p>
          {t.description && <p className="mt-0.5 text-sm text-muted">{t.description}</p>}
        </div>
        <button
          type="button"
          onClick={() => dismiss(t.id)}
          className="-mr-1 rounded p-1 text-faint hover:text-fg"
          aria-label="Dismiss notification"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    );
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end">
        <div role="status" aria-live="polite" className="flex w-full max-w-sm flex-col gap-2">
          {toasts.filter((t) => t.tone !== 'error').map(renderToast)}
        </div>
        <div role="alert" aria-live="assertive" className="flex w-full max-w-sm flex-col gap-2">
          {toasts.filter((t) => t.tone === 'error').map(renderToast)}
        </div>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
